import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The TXT or “descriptive text” DNS record type was created to hold human-readable text. It now plays a critical role in the prevention of spam on the Internet.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The TXT record type was introduced in the original DNS specifications ("),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1035",
    rel: "noopener noreferrer"
  }, "1035"),
  /*#__PURE__*/_createTextVNode(") in 1987. They were to be used for notes and text created by DNS administrators. There was originally no definitive purpose for TXT records. They were used for whatever information the DNS administrator thought was useful. This included contact information, the locations and owners of machines, humorous messages, and other administrivia. ")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-record-types/txt/the-txt-record-type.jpg",
    alt: "Formatless TXT records have become messy over the years",
    title: "Formatless TXT records have become messy over the years",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Formatless TXT records have become messy over the years. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", { id: "txt-record-format" }, "TXT record format", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1035",
    rel: "noopener noreferrer"
  }, "RFC 1035"),
  /*#__PURE__*/_createTextVNode(" definition of the record data for TXT record is one or more “character-strings”. Each character string can be at most 255 characters long.")
], -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("TXT records also have a Time-to-Live value in seconds, like all other DNS records. A simple example TXT record for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" with a TTL of 3600 seconds (one hour) might look like this:")
], -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"Welcome to the example domain!\"", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "There are a few somewhat confusing aspects of TXT records. Namely, the use of quotation marks and multiple character strings. Quotation marks are not necessary if you are working with TXT records in a DNS provider's web portal. When editing a traditional zone file, quotation marks are required around each string.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, "Each TXT record can contain multiple character strings. Each character string may contain up to 255 characters. Multiple TXT records may also exist at a single name in the DNS. See our 'related questions' section below for more details.", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, "The examples on this page will use DNS zone file format so will contain quotation marks.", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("h2", null, "A real world TXT example", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("code", null, "icann.org", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("pre", null, ";; ->>HEADER<<- opcode: QUERY, status: NOERROR, id: 18975\n;; flags: qr rd ra; QUERY: 1, ANSWER: 1, AUTHORITY: 0, ADDITIONAL: 1\n\n;; OPT PSEUDOSECTION:\n; EDNS: version: 0, flags:; udp: 512\n;; QUESTION SECTION:\n;icann.org.                     IN      TXT\n\n;; ANSWER SECTION:\nicann.org.              600     IN      TXT     \"v=spf1 ip4:192.0.32.0/20 ip4:199.91.192.0/21 ip4:64.78.40.0/27 ip4:162.216.194.0/27 ip4:64.78.33.5/32 ip4:64.78.33.6/31 ip4:64.78.48.205/32 ip4:64.78.48.206/31\"\n                                                \" ip6:2620:0:2d0::0/48 ip6:2620:0:2830::0/48 ip6:2620:0:2ed0::0/48 include:salesforce.icann.org -all\"", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "The DNS administrator for ICANN has followed SPF requirements and created a single TXT record with two strings. There are two strings because the total length of the SPF record is longer than 255 characters.", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "Also note that quotation marks surround each character string. These quotation marks are not part of the TXT data. Dig included them so we can see where each string begins and ends.", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h2", null, "Uses of the TXT record type", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, "The TXT record was intended for free-form text entered by DNS administrators. Use of the TXT record has been expanded to other applications over the years.", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "Today, TXT records are mainly used for:", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM"),
  /*#__PURE__*/_createTextVNode(" — Domain Keys Identified Mail")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "DMARC"),
  /*#__PURE__*/_createTextVNode(" — Domain-based Message Authentication, Reporting & Conformance")
], -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "MTA-STS"),
  /*#__PURE__*/_createTextVNode(" — SMTP MTA Strict Transport Security")
], -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Domain verification")
], -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "DNS-SD"),
  /*#__PURE__*/_createTextVNode(" — DNS Service Discovery")
], -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("h3", null, "Sender Policy Framework (SPF)", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("a", {
  href: "https://datatracker.ietf.org/doc/html/rfc7208",
  rel: "noopener noreferrer"
}, "RFC 7208", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("a", {
  href: "https://datatracker.ietf.org/doc/html/rfc7208#section-3.1",
  rel: "noopener noreferrer"
}, "RFC 7208 section 3.1", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Below is a very simple SPF record example. This record states that the mail servers at the IP addresses 10.0.0.1 and 10.0.0.2 are permitted to send mail for the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" domain:")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 ip4:10.0.0.1 ip4:10.0.0.2 -all\"", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("h3", null, "Domain Keys Identified Mail (DKIM)", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DKIM is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc6376",
    rel: "noopener noreferrer"
  }, "RFC 6376"),
  /*#__PURE__*/_createTextVNode(". DKIM uses DNS TXT records to publish the public key from a domain's public-private key pair. This public key is used to securely validate emails signed with a digital signature.")
], -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Unlike SPF records, the DNS TXT record for DKIM is not created at the root of the domain. The string following \"p=\" in the record data of the TXT is a public key. An example DKIM record for the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" domain is shown below. (The public key is truncated for readability.)")
], -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("pre", null, "default._domainkey.example.org. 3600 TXT \"v=DKIM1;t=s;p=MIGfMA0GCSqGS...\"", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("h3", null, "Domain-based Message Authentication, Reporting & Conformance (DMARC)", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DMARC is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7489",
    rel: "noopener noreferrer"
  }, "RFC 7489"),
  /*#__PURE__*/_createTextVNode(". DMARC uses DNS TXT records to specify email policy for a domain. The policy specifies what action to take if SPF or DKIM checks fail on email received from the domain.")
], -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, "Like the DKIM record, the DMARC record is not created at root of the domain. An example is shown below. This example specifies that mail which does not pass DMARC checks should be placed in quarantine. Reports should be sent to admin@example.org.", -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("pre", null, "_dmarc.example.org. 3600 TXT \"v=DMARC1; p=quarantine; rua=mailto:admin@example.org\"", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("h3", null, "SMTP MTA Strict Transport Security (MTA-STS)", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("MTA-STS is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc8461",
    rel: "noopener noreferrer"
  }, "RFC 8461"),
  /*#__PURE__*/_createTextVNode(". MTA-STS allows a mail service provider to declare it accepts Transport Layer Security (TLS) to secure SMTP connections. It can also specify if other SMTP servers should refuse to deliver email to the domain if TLS is unavailable.")
], -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Below is a simple example of a TXT record for MTA-STS. The existence of this record signals that SMTP servers should attempt to retrieve the MTA-STS policy for the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" domain using an HTTPS get request. This record also gives the current policy ID.")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("pre", null, "_mta-sts.example.org. 3600 TXT \"v=STSv1; id=20220601120000Z;\"", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("h3", null, "Domain verification", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "In recent years it has become popular to use a DNS TXT record to prove ownership of a domain. A service provider gives the domain's administrator a challenge string. The administrator must publish the challenge string in a TXT record in their DNS zone. The service provider performs a DNS query for the TXT record and verifies that the challenge string has been published. This proves that the administrator controls DNS for the domain.", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("p", null, "The format of the domain verification TXT record varies with provider. Always follow your provider's instructions exactly. The name of the service is usually included in the challenge string, so to verify a domain with Google the record might look something like this:", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"google=6e6922db-e3e6-4a36-904e-a805c28087fa\"", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The IETF draft "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://www.ietf.org/archive/id/draft-sahib-domain-verification-techniques-03.html",
    rel: "noopener noreferrer"
  }, "sahib-domain-verification-techniques"),
  /*#__PURE__*/_createTextVNode(" contains a survey of current uses of this method.")
], -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("h3", null, "DNS Service Discovery (DNS-SD)", -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DNS-SD is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc6763",
    rel: "noopener noreferrer"
  }, "RFC 6763"),
  /*#__PURE__*/_createTextVNode(". DNS-SD allows services to be discovered by application clients through the DNS. This is an alternative to SRV records. The record data of the TXT record contains a set of key/value pairs preceded by length bytes. DNS-SD is not currently in widespread use.")
], -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("h2", null, "Finding TXT records for a zone", -1)
const _hoisted_49 = /*#__PURE__*/_createElementVNode("p", null, "The TXT records for a zone can be queried with the dig or nslookup command line tools. To find the mail servers for wikipedia.org, use this command:", -1)
const _hoisted_50 = /*#__PURE__*/_createElementVNode("pre", null, "dig TXT wikipedia.org", -1)
const _hoisted_51 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_52 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=txt wikipedia.org", -1)
const _hoisted_53 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_54 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_55 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_56 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_57 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Should I enter quotation marks in TXT records? ", -1)
const _hoisted_58 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_59 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "You need quotation marks when working with standard DNS zone files but not when typing TXT data into a DNS provider's web portal. Consult your DNS provider's documentation if you are unsure.")
], -1)
const _hoisted_60 = /*#__PURE__*/_createElementVNode("p", null, "If you were to look at a DNS message as it travels across the Internet, you would not find quotation marks around TXT character strings.", -1)
const _hoisted_61 = /*#__PURE__*/_createElementVNode("p", null, "Quotation marks around the character strings are required in DNS zone files. The DNS specifications call this “presentation format”. The quotation marks are not treated as part of the TXT record data.", -1)
const _hoisted_62 = /*#__PURE__*/_createElementVNode("p", null, "A typical TXT record in a zone file might look like this:", -1)
const _hoisted_63 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"apple banana pear\"", -1)
const _hoisted_64 = /*#__PURE__*/_createElementVNode("p", null, "This TXT record's data is a single character string consisting of three words separated by spaces. When the file is read, the quotation marks are discarded.", -1)
const _hoisted_65 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How many TXT records should I have? ", -1)
const _hoisted_66 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_67 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "The DNS specification allows multiple TXT records to exist. For human-readable data you can use any combination of multiple strings and multiple TXT records.")
], -1)
const _hoisted_68 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("TXT records for other applications must follow required standards. Using SPF as an example, "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7208#section-3",
    rel: "noopener noreferrer"
  }, "RFC 7208 section 3"),
  /*#__PURE__*/_createTextVNode(" specifies that a single TXT record must be used for SPF. SPF data longer than 255 characters must be broken up into multiple strings.")
], -1)
const _hoisted_69 = /*#__PURE__*/_createElementVNode("p", null, "It is common to see multiple TXT records today in the DNS. One TXT might be for SPF and others might exist for domain verification or other uses. For example this record set contains a SPF record and a domain verification record:", -1)
const _hoisted_70 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 TXT \"v=spf1 ip4:10.0.0.1 ip4:10.0.0.2 -all\"\nexample.org. 3600 TXT \"google=6e6922db-e3e6-4a36-904e-a805c28087fa\"", -1)
const _hoisted_71 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How large can TXT records be? ", -1)
const _hoisted_72 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_73 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "The RFCs do not impose a size limit on TXT records but there are practical considerations that are important.")
], -1)
const _hoisted_74 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DNS can be transported over UDP or TCP. It is best to keep the total size of the TXT record set small enough that TCP is not required. If possible, keep the total size of the DNS response message below 512 bytes. However this may not always be practical. Most modern DNS resolvers support UDP payloads larger than 512 bytes. A more realistic goal for the DNS response size is 1232 bytes. "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://dnsflagday.net",
    rel: "noopener noreferrer"
  }, "DNS Flag Day"),
  /*#__PURE__*/_createTextVNode(" is an industry effort to set a uniform and safe upper bound for DNS UDP payload size.")
], -1)
const _hoisted_75 = /*#__PURE__*/_createElementVNode("p", null, "Exceeding a DNS response size of 1232 bytes for DNS queries for your domain is problematic for these reasons:", -1)
const _hoisted_76 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "DNS requests made by legitimate clients will take longer to complete. This increases latency."),
  /*#__PURE__*/_createElementVNode("li", null, "Malicious parties may use the domain as part of DNS Distributed Denial-of-Service (DDoS) attacks. Botnets may query the domain in an attempt to overwhelm resolvers with expensive TCP requests.")
], -1)
const _hoisted_77 = /*#__PURE__*/_createElementVNode("p", null, "Limiting the amount of TXT data as much as possible reduces latency and protects against abuse.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DnsRecordTypes from '@/views/learning/DnsRecordTypes.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'DNS record types',
  url: '/learning/dns-record-types/txt/',
  breadcrumbs: [learningBreadcrumb, DnsRecordTypes.breadcrumb],
  title: 'The TXT record',
  description: 'The TXT or “descriptive text” DNS record type was created to hold human-readable text but now plays a critical role in the prevention of spam on the Internet.',
  overviewImage: '/img/learning/dns-record-types/txt/the-txt-record-type-small.jpg',
  overviewImageAlt: 'Mail authentication before TXT records',
  author: jeff,
  editor: ruurtjan,
  overviewDescription: 'The TXT or “descriptive text” DNS record type was created to hold human-readable text but now plays a critical role in the prevention of spam on the Internet.',
  img1024x512: 'https://www.nslookup.io/img/learning/dns-record-types/txt/the-txt-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-record-types/txt/the-txt-record-social.jpg',
  written: new Date('2022-06-21T13:26:46.745Z'),
  updated: new Date('2023-03-28T11:58:24.570Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecordTypeTxt',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/txt/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dns-record-types/txt/the-txt-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _createElementVNode("p", null, [
          _createTextVNode("TXT records were used this way until 2003. In 2003 efforts began to fight back against spam and other abuses of email. This led to the creation of "),
          _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("SPF (Sender Policy Framework)")
            ]),
            _: 1
          }),
          _createTextVNode(". SPF stores email authority information in TXT records. Other uses for the TXT record type have been added over the years.")
        ]),
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_7
          ]),
          _: 1
        }),
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _createElementVNode("p", null, [
          _createTextVNode("Let's examine the actual TXT response for "),
          _hoisted_12,
          _createTextVNode(". At the time this page was written, the TXT response using dig or our "),
          _createVNode(_component_router_link, { to: "/txt-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("TXT lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool is:")
        ]),
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_13
          ]),
          _: 1
        }),
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _createElementVNode("ul", null, [
          _createElementVNode("li", null, [
            _createElementVNode("strong", null, [
              _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
                default: _withCtx(() => [
                  _createTextVNode("SPF")
                ]),
                _: 1
              })
            ]),
            _createTextVNode(" — Sender Policy Framework")
          ]),
          _hoisted_19,
          _hoisted_20,
          _hoisted_21,
          _hoisted_22,
          _hoisted_23
        ]),
        _hoisted_24,
        _createElementVNode("p", null, [
          _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("SPF")
            ]),
            _: 1
          }),
          _createTextVNode(" is specified in "),
          _hoisted_25,
          _createTextVNode(". SPF uses DNS TXT records to list the mail servers permitted to send email for a domain. The creators of SPF used TXT records out of convenience. There was an attempt to create a dedicated DNS record type for SPF but this was officially deprecated in "),
          _hoisted_26,
          _createTextVNode(".")
        ]),
        _hoisted_27,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_28
          ]),
          _: 1
        }),
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_32
          ]),
          _: 1
        }),
        _hoisted_33,
        _hoisted_34,
        _hoisted_35,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_36
          ]),
          _: 1
        }),
        _hoisted_37,
        _hoisted_38,
        _hoisted_39,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_40
          ]),
          _: 1
        }),
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_44
          ]),
          _: 1
        }),
        _hoisted_45,
        _hoisted_46,
        _hoisted_47,
        _hoisted_48,
        _hoisted_49,
        _hoisted_50,
        _hoisted_51,
        _hoisted_52,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the TXT records of any domain name in our "),
          _createVNode(_component_router_link, { to: "/txt-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("TXT lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool, or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_53, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find TXT records",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_54, [
          _hoisted_55,
          _createElementVNode("div", _hoisted_56, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_57,
                    _createElementVNode("span", _hoisted_58, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_59,
                    _hoisted_60,
                    _hoisted_61,
                    _hoisted_62,
                    _hoisted_63,
                    _hoisted_64
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_65,
                    _createElementVNode("span", _hoisted_66, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_67,
                    _hoisted_68,
                    _hoisted_69,
                    _hoisted_70
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_71,
                    _createElementVNode("span", _hoisted_72, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_73,
                    _hoisted_74,
                    _hoisted_75,
                    _hoisted_76,
                    _hoisted_77
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})